import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import WarningIcon from "@mui/icons-material/Warning";
import BalanceIcon from "@mui/icons-material/Balance";

import {
  StatCard,
  LineChart,
  DonutChart,
  CombinedBarLineChart,
} from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  DashboardCardSkeleton,
  CohortGuard,
} from "../../../../../shared/components";
import {
  useOverallStressStats,
  useOverallStressTrends,
  useOverallStressDistribution,
  useOverallStressPatterns,
} from "../hooks";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
} from "../../../../../shared/hooks";

const OVERALL_STRESS_AND_MIND_DETAILS = {
  stressAndRecoveryTrends: `Displays daily variation in stress and recovery metrics over time. Stress reflects physiological load and sympathetic activation, while recovery indicates autonomic balance and restorative capacity.

Tracking these trends helps identify patterns of sustained stress, periods of effective recovery, and overall shifts in wellbeing across the organisation.`,
  stressLevelDistribution: `Displays the proportion of employees within high, medium, and low stress zones, providing a snapshot of overall stress burden across the organisation. Stress levels are typically derived from physiological indicators such as heart rate patterns and heart rate variability, reflecting autonomic nervous system activity.

This distribution view helps identify the concentration of employees experiencing elevated physiological stress, monitor shifts in stress exposure over time, and assess whether recovery strategies and workload management are effectively supporting workforce wellbeing.`,
  weeklyPatterns: `Compares average stress levels and heart rate variability (HRV) trends across the week, highlighting how physiological load and autonomic recovery fluctuate day by day.

This view helps identify recurring high-stress periods, recovery dips, and differences between weekdays and weekends, supporting better workload planning and recovery optimisation.`,
};

export const OverallStressAndMind: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } =
    usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [trendView, setTrendView] = useState("stress_score");

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      stress_score: {
        min: typeof metric_min.stress_score === "number" ? metric_min.stress_score : undefined,
        max: typeof metric_max.stress_score === "number" ? metric_max.stress_score : undefined,
      },
      hrv: {
        min: typeof metric_min.hrv === "number" ? metric_min.hrv : undefined,
        max: typeof metric_max.hrv === "number" ? metric_max.hrv : undefined,
      },
      stress_recovery_balance: {
        min: typeof metric_min.stress_recovery_balance === "number" ? metric_min.stress_recovery_balance : undefined,
        max: typeof metric_max.stress_recovery_balance === "number" ? metric_max.stress_recovery_balance : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  // Stats
  const statsQuery = useOverallStressStats({
    ...filterParams,
    metrics: [
      "stress_score",
      "high_stress_days",
      "hrv",
      "stress_recovery_balance",
    ],
  });

  // Trends
  const trendsQuery = useOverallStressTrends({
    ...filterParams,
    metrics: ["stress_score", "stress_recovery_balance", "hrv"],
    granularity,
  });

  // Distribution
  const distributionQuery = useOverallStressDistribution(filterParams);

  // Patterns - We need to fetch both to combine them
  const stressPatternsQuery = useOverallStressPatterns({
    ...filterParams,
    metric: "stress_score_patterns",
    granularity: "weekly", // Assuming weekly patterns for the bottom chart
  });

  const hrvPatternsQuery = useOverallStressPatterns({
    ...filterParams,
    metric: "hrv_patterns",
    granularity: "weekly",
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  // Transform Trends Data
  const trendsData = useMemo(() => {
    return transformTrendsData(trendsQuery.data, [
      "stress_score",
      "stress_recovery_balance",
      "hrv",
    ]);
  }, [trendsQuery.data]);

  // Transform Distribution Data
  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color:
        item.name === "high" || item.label.toLowerCase().includes("high")
          ? theme.palette.warning.main
          : item.name === "medium" ||
            item.label.toLowerCase().includes("medium")
          ? theme.palette.success.main
          : theme.palette.info.main,
    }));
  }, [distributionQuery.data, theme]);

  // Transform Patterns Data
  // Merging stress and hrv patterns by day
  const weeklyPatternsData = useMemo(() => {
    const stress = stressPatternsQuery.data?.data?.patterns || [];
    const hrv = hrvPatternsQuery.data?.data?.patterns || [];

    // Create a map by day
    const combined = new Map<
      string,
      { day: string; stressScore?: number; hrv?: number }
    >();

    stress.forEach((p) => {
      const key = p.day || p.time || "";
      if (key) {
        combined.set(key, { day: key, stressScore: p.value ?? undefined });
      }
    });

    hrv.forEach((p) => {
      const key = p.day || p.time || "";
      if (key) {
        const existing = combined.get(key) || { day: key };
        combined.set(key, { ...existing, hrv: p.value ?? undefined });
      }
    });

    return Array.from(combined.values());
  }, [stressPatternsQuery.data, hrvPatternsQuery.data]);

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      {
        metric: stats.stress_score,
        icon: SelfImprovementIcon,
      },
      {
        metric: stats.hrv,
        icon: MonitorHeartIcon,
      },
      {
        metric: stats.high_stress_days,
        icon: WarningIcon,
      },
      {
        metric: stats.stress_recovery_balance,
        icon: BalanceIcon,
      },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const getTrendLabel = () => {
    switch (trendView) {
      case "stress_score":
        return "Stress Score";
      case "hrv":
        return "Heart Rate Variability";
      case "stress_recovery_balance":
        return "Stress-Recovery Balance";
      default:
        return "Metric";
    }
  };

  const getTrendColor = () => {
    switch (trendView) {
      case "stress_score":
        return theme.palette.warning.main;
      case "hrv":
        return theme.palette.info.main;
      case "stress_recovery_balance":
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getYAxisMax = () => {
    switch (trendView) {
      case "stress_score":
        return 5;
      case "hrv":
        return 100; // Adjust based on expected range
      case "stress_recovery_balance":
        return 100;
      default:
        return 100;
    }
  };

  const isLoading =
    statsQuery.isLoading ||
    trendsQuery.isLoading ||
    distributionQuery.isLoading ||
    stressPatternsQuery.isLoading ||
    hrvPatternsQuery.isLoading;

  const hasError =
    statsQuery.isError ||
    trendsQuery.isError ||
    distributionQuery.isError ||
    stressPatternsQuery.isError ||
    hrvPatternsQuery.isError;

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && (
          <Alert severity="error">
            Failed to load data. Please try again later.
          </Alert>
        )}

        <Grid container spacing={3}>
          {isLoading && statCards.length === 0 ? (
            <>
              {[1, 2, 3, 4].map((index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <DashboardCardSkeleton />
                </Grid>
              ))}
            </>
          ) : (
            statCards.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))
          )}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Stress & Recovery Trends"
              subtitle="Daily variation of stress and recovery metrics."
              details={OVERALL_STRESS_AND_MIND_DETAILS.stressAndRecoveryTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={trendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setTrendView(newValue);
                  }}
                  size="small"
                >
                  <ToggleButton value="stress_score">Stress Score</ToggleButton>
                  <ToggleButton value="hrv">
                    HRV (Stress Indicator)
                  </ToggleButton>
                  <ToggleButton value="stress_recovery_balance">
                    Stress-Recovery Balance
                  </ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : trendsData.length > 0 ? (
                  <LineChart
                    data={trendsData}
                    xAxisKey="label"
                    series={[
                      {
                        dataKey: trendView,
                        label: getTrendLabel(),
                        color: getTrendColor(),
                        area: true,
                      },
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[trendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[trendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No trend data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Stress Level Distribution"
              subtitle="Proportion of employees within high, medium, and low stress zones."
              details={OVERALL_STRESS_AND_MIND_DETAILS.stressLevelDistribution}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : distributionData.length > 0 ? (
                  <DonutChart data={distributionData} height={300} />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No distribution data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Weekly Patterns"
              subtitle="Comparison of average stress and HRV trends throughout the week."
              details={OVERALL_STRESS_AND_MIND_DETAILS.weeklyPatterns}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : weeklyPatternsData.length > 0 ? (
                  <CombinedBarLineChart
                    data={weeklyPatternsData}
                    xAxisKey="day"
                    barSeries={[
                      {
                        dataKey: "stressScore",
                        label: "Stress Score",
                        color: theme.palette.warning.main,
                      },
                    ]}
                    lineSeries={[
                      {
                        dataKey: "hrv",
                        label: "HRV (ms)",
                        color: theme.palette.info.main,
                      },
                    ]}
                    height={300}
                    barYAxisMin={0}
                    barYAxisMax={5}
                    lineYAxisMin={0}
                    lineYAxisMax={100}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
